import { useClerk, useUser } from "@clerk/clerk-react";
import { ChevronDown, LogOut, Menu, MessageCircle, Search, Shield, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChatSidebar } from "@/contexts/ChatSidebarContext";
import { useMobileSidebar } from "@/contexts/MobileSidebarContext";
import { isAdminRole, isAssessorRole } from "@/lib/roles";
import { NotificationBell } from "./NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";

export function TopBar() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { isOpen, toggle } = useMobileSidebar();
  const chatSidebar = useChatSidebar();

  const role = user?.publicMetadata?.role as string | undefined;
  const isAdmin = isAdminRole(role);
  const isAssessor = isAssessorRole(role);

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.emailAddresses[0]?.emailAddress.split("@")[0] ||
    "User";

  return (
    <div className="h-[calc(4rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] flex-none bg-background border-b border-content-border px-4 md:px-7 flex items-center gap-4">
      {/* Mobile Menu Button */}
      <button
        onClick={toggle}
        className="md:hidden w-[38px] h-[38px] rounded-full border border-content-border bg-card flex items-center justify-center hover:border-brand/40 transition-colors"
        aria-label={isOpen ? "Close menu" : "Open menu"}
      >
        {isOpen ? (
          <X className="w-4 h-4 text-content-foreground" />
        ) : (
          <Menu className="w-4 h-4 text-content-foreground" />
        )}
      </button>

      {/* Search Bar - Hidden on mobile */}
      <div className="hidden md:flex w-[420px] max-w-[44%] items-center gap-2.5 rounded-full border border-content-border bg-card px-4 py-[9px]">
        <Search className="w-[15px] h-[15px] flex-none text-content-muted" strokeWidth={1.8} />
        <Input
          type="text"
          placeholder="Search…"
          className="h-auto w-full border-none bg-transparent p-0 text-[13.5px] text-content-foreground shadow-none placeholder:text-content-muted focus-visible:ring-0"
        />
      </div>

      {/* Right Side */}
      <div className="ml-auto flex items-center gap-2.5">
        <NotificationBell />

        {/* Chat Icon */}
        {chatSidebar && (
          <button
            onClick={chatSidebar.toggle}
            className="w-[38px] h-[38px] rounded-full border border-content-border bg-card flex items-center justify-center hover:border-brand/40 transition-colors"
            aria-label="Open chat"
          >
            <MessageCircle className="w-4 h-4 text-content-foreground" strokeWidth={1.7} />
          </button>
        )}

        {/* User Info */}
        <div className="ml-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 rounded-full border border-content-border bg-card py-[5px] pl-1.5 pr-3 transition-colors hover:border-brand/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
                <Avatar className="h-[30px] w-[30px]">
                  <AvatarImage src={user?.imageUrl} />
                  <AvatarFallback className="bg-gradient-to-br from-brand to-primary text-[11px] font-bold text-[#F2EFE3]">
                    {user?.firstName?.[0] ||
                      user?.emailAddresses[0]?.emailAddress[0]?.toUpperCase() ||
                      "U"}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-[13.5px] font-semibold text-content-foreground">
                  {displayName}
                </span>
                <ChevronDown className="w-3 h-3 text-content-muted" strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[232px]">
              <div className="border-b border-content-border/60 px-3 pb-3 pt-2.5 mb-1">
                <div className="text-[13.5px] font-bold text-content-foreground">{displayName}</div>
                <div className="mt-0.5 text-xs text-content-muted">
                  {user?.emailAddresses[0]?.emailAddress}
                </div>
              </div>
              {isAdmin && (
                <>
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <Shield className="w-4 h-4" />
                    Admin Interface
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {isAssessor && (
                <>
                  <DropdownMenuItem onClick={() => navigate("/admin/reviews")}>
                    <Shield className="w-4 h-4" />
                    Review Queue
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => signOut()}>
                <LogOut className="w-4 h-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
